import React, { useContext, useRef, useState } from "react";
import { Callout, DirectionalHint } from "@fluentui/react";

import { SettingsContext } from "../../Context/SettingsContext";

import { BottomPanelCloseIcon, BottomPanelOpenIcon, HelpIcon, SettingsIcon } from "./HeaderIcons";

import styles from "./HeaderActions.module.css";

const supportLinks = [
    { label: "Support Page", href: "https://support.ironworksecm.com" },
    { label: "Knowledge Base", href: "https://support.ironworksecm.com/knowledge-base" },
    { label: "Example Questions", href: "https://support.ironworksecm.com/knowledge-base/idm-data-mining" }
];

type HeaderActionsProps = {
    /** Omitted when there is no history to show, which hides the toggle entirely. */
    onToggleChatHistory?: () => void;
    isChatHistoryOpen?: boolean;
};

/**
 * The ICE top-bar equivalent: a settings gear and a help button, right-aligned.
 * ICE names its gear per module ("IMC Settings"), hence "IDM Settings" here.
 *
 * The chat-history toggle joins this cluster so the header holds one group of
 * icon buttons rather than leaving it stranded beside the logo. It is the only
 * item here that is narrow-viewport only — from 640px up the drawer is always open.
 */
export const HeaderActions = ({ onToggleChatHistory, isChatHistoryOpen }: HeaderActionsProps) => {
    const { setIsSettingsOpened } = useContext(SettingsContext);
    const supportRef = useRef<HTMLButtonElement>(null);
    const [isSupportOpen, setIsSupportOpen] = useState(false);

    return (
        // Fluent's Callout leaves a zero-width <span class="ms-layer"> anchor in place.
        // Inside the flex row that span became a fourth item, and the 12px gap in front of
        // it shunted the right-aligned buttons 12px left whenever the popover opened. This
        // block wrapper keeps the anchor out of the flex flow.
        <div className={styles.root}>
            <div className={styles.actions}>
                {onToggleChatHistory && (
                    <button
                        type="button"
                        className={`${styles.action} ${styles.actionNarrowOnly}`}
                        title={isChatHistoryOpen ? "Hide chat history" : "Show chat history"}
                        aria-label={isChatHistoryOpen ? "Hide chat history" : "Show chat history"}
                        aria-expanded={isChatHistoryOpen}
                        onClick={onToggleChatHistory}
                    >
                        {isChatHistoryOpen ? <BottomPanelCloseIcon /> : <BottomPanelOpenIcon />}
                    </button>
                )}
                <button type="button" className={styles.action} title="IDM Settings" aria-label="IDM Settings" onClick={() => setIsSettingsOpened(true)}>
                    <SettingsIcon />
                </button>
                <button
                    ref={supportRef}
                    type="button"
                    className={styles.action}
                    title="Support"
                    aria-label="Support"
                    aria-haspopup="menu"
                    aria-expanded={isSupportOpen}
                    onClick={() => setIsSupportOpen(open => !open)}
                >
                    <HelpIcon />
                </button>
            </div>
            {isSupportOpen && (
                <Callout
                    target={supportRef}
                    onDismiss={() => setIsSupportOpen(false)}
                    directionalHint={DirectionalHint.bottomRightEdge}
                    gapSpace={8}
                    beakWidth={16}
                    // The panel below carries the surface; Fluent's own chrome is cleared out
                    // so there is no white card behind ICE's dark popover styling.
                    styles={{
                        root: { background: "transparent", border: "none", boxShadow: "none" },
                        calloutMain: { background: "transparent", borderRadius: "var(--idm-radius-panel)" },
                        beak: { background: "var(--idm-color-dark)" },
                        beakCurtain: { background: "transparent" }
                    }}
                >
                    <div className={styles.supportPanel}>
                        <h2 className={styles.supportHeading}>Support</h2>
                        <ul className={styles.supportList}>
                            {supportLinks.map(link => (
                                <li key={link.href}>
                                    <a className={styles.supportLink} href={link.href} target="_blank" rel="noopener noreferrer">
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </Callout>
            )}
        </div>
    );
};
